import React, { useState, useMemo, useCallback } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Drawer, Button, Form, Modal } from "antd";
import { connect } from "dva";
import { GatePassStatus, Drivability, VehicleColor } from "@/utils/enum";
import { uploadGatePass } from "@/utils/gatepass";
import { hideJiraWidget, showJiraWidget, getUrlParams, parseAsNumber } from "@/utils/utils";
import useScreenDetector from "@/hooks/useScreenDetector";
import DrawerWrapper from "@/components/DrawerWrapper";
import MarginFeedbackAlert from "@/components/MarginFeedbackAlert";
import VehicleForm from "@/forms/shared/Vehicle/VehicleForm";

const { confirm } = Modal;

const defaultVehicleValues = {
  inoperational: Drivability.RideAndDrive,
  gatePassStatus: GatePassStatus.NotRequired,
};

const AddVehicleForm = ({ isAdmin, dispatch, adminShipmentDetails, shipmentDetails, configPerm, refreshData }) => {
  const { isMobile } = useScreenDetector(1100);
  const [visible, setVisible] = useState(false);
  const [loadingAddVehicle, setLoadingAddVehicle] = useState(false);

  const shipmentData = useMemo(
    () => (isAdmin ? adminShipmentDetails : shipmentDetails),
    [isAdmin, adminShipmentDetails, shipmentDetails],
  );

  const dispatchTypeAddVehicle = useMemo(
    () => (isAdmin ? "adminShipmentView/addVehicleShipment" : "shipmentDetails/addVehicleShipment"),
    [isAdmin],
  );

  const [vehicleFormRef] = Form.useForm();

  const closeDrawer = useCallback(() => {
    showJiraWidget();
    vehicleFormRef.resetFields();
    setVisible(false);
  }, [vehicleFormRef]);

  const checkIfFormIsDirty = useCallback(() => {
    if (vehicleFormRef.isFieldsTouched()) {
      // close drawer
      confirm({
        title: `Are you sure you want to cancel?`,
        content: "Unsaved data will be lost.",
        okText: "Discard Changes",
        onOk: () => {
          closeDrawer();
        },
      });
    } else {
      closeDrawer();
    }
  }, [vehicleFormRef, closeDrawer]);

  const showDrawer = useCallback(() => {
    setVisible(true);
    hideJiraWidget();
  }, []);

  const onClose = useCallback(
    event => {
      const classList = Array.from(event?.currentTarget?.classList ?? []);
      if (classList.indexOf("ant-drawer-mask") !== -1) {
        // closing by clicking on the overlay
        checkIfFormIsDirty();
      } else {
        // closing by clicking on the close button, continue with the normal flow
        closeDrawer();
      }
    },
    [checkIfFormIsDirty, closeDrawer],
  );

  const onCancel = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const onSubmit = useCallback(
    async result => {
      const shipmentId = getUrlParams("shipmentId");

      setLoadingAddVehicle(true);
      let gatePassObj = { url: null };

      if (result.gatePassStatus === GatePassStatus.Attached) {
        const gatePassResponse = await uploadGatePass(result.gatePassFile);

        if (gatePassResponse?.error) {
          setLoadingAddVehicle(false);
          return;
        }

        gatePassObj = gatePassResponse;
      }

      const usingCustomVehicleColor = result?.color === VehicleColor.Other;

      const vehiclePayload = {
        dealerId: shipmentData.data.dealerId,
        vin: result.vin?.toUpperCase(),
        year: result.year,
        make: result.make,
        model: result.model,
        vehicleType: result.vehicleType,
        trimPackage: result.trimPackageName,
        drivabilityType: result.inoperational,
        stockNumber: result.stockNumber,
        curbWeight: result.curbWeight,
        bodySubType: result.bodySubType,
        width: parseAsNumber(result?.width, 0),
        height: parseAsNumber(result?.height, 0),
        length: parseAsNumber(result?.length, 0),
        exteriorDimensions: result.exteriorDimensions,
        color: usingCustomVehicleColor ? result?.customColor : result?.color,
        otherType: result.vehicleTypeComment,
        comments: result.comments,
        truckWithDualWheels: result.truckWithDualWheels,
        truckWithLongWheelBase: result.truckWithLongWheelBase,
        isEV: result.isEV,
        shipmentId,
        gatePassType: result.gatePassStatus,
        gatePassUrl: gatePassObj.url,
        msrp: result.msrp,
        dealerPrice: result.dealerPrice,
        transporterPrice: result.transporterPrice,
        dealerPriceFeedbackType: result.dealerPriceFeedbackType,
        transporterPriceFeedbackType: result.transporterPriceFeedbackType,
        dealerOtherFeedbackNote: result.dealerOtherFeedbackNote,
        transporterOtherFeedbackNote: result.transporterOtherFeedbackNote,
      };

      dispatch({
        type: dispatchTypeAddVehicle,
        vehiclePayload,
      })
        .then(res => {
          if (res && !res.error) {
            closeDrawer();
            refreshData?.();
          }
        })
        .finally(() => {
          setLoadingAddVehicle(false);
        });
    },
    [shipmentData?.data?.dealerId, dispatchTypeAddVehicle, closeDrawer, dispatch, refreshData],
  );

  const isPricingSectionVisible = useMemo(
    () => isAdmin && !!configPerm["PricingService.QuoteRuleResults"],
    [isAdmin, configPerm],
  );

  const dealerPrice = Form.useWatch("dealerPrice", vehicleFormRef);
  const transporterPrice = Form.useWatch("transporterPrice", vehicleFormRef);

  return (
    <>
      <Button type="primary" onClick={showDrawer} icon={<PlusOutlined />}>
        {!isMobile && "Add Vehicles"}
      </Button>
      <DrawerWrapper visible={visible}>
        <Drawer
          key={visible ? "open" : "closed"}
          title="Add vehicle to Shipment"
          width={isMobile ? "80%" : "40%"}
          onClose={onClose}
          open={visible}
          bodyStyle={{ paddingBottom: 80 }}
          footer={
            <section style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {isPricingSectionVisible && (
                <section>
                  <MarginFeedbackAlert dealerPrice={Number(dealerPrice)} transporterPrice={Number(transporterPrice)} />
                </section>
              )}

              <div style={{ textAlign: "right" }}>
                <Button onClick={onCancel} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button onClick={vehicleFormRef.submit} type="primary" htmlType="submit" loading={loadingAddVehicle}>
                  Add
                </Button>
              </div>
            </section>
          }
        >
          <VehicleForm
            defaultValues={defaultVehicleValues}
            configPerm={configPerm}
            formRef={vehicleFormRef}
            onSubmit={onSubmit}
            shipmentData={shipmentData?.data}
          />
        </Drawer>
      </DrawerWrapper>
    </>
  );
};

export default connect(({ adminShipmentView, shipmentDetails, configs }) => ({
  configPerm: configs.configPerm,
  adminShipmentDetails: adminShipmentView,
  shipmentDetails,
}))(AddVehicleForm);
